/**
 * The training-record surfaces that no destination root can compose.
 *
 * Two providers live here, and both are here for the same structural reason:
 * the surfaces they serve are **full-viewport routes**, which `AppShell` mounts
 * beside the four destination roots rather than inside one. A provider placed in
 * a destination root is therefore invisible to them, and until this file existed
 * neither was placed anywhere outside a test harness.
 *
 * **`SummaryPresentationProvider`.** `/train/summary` is in `ROUTE_TABLE`, and
 * `present()` is the only thing that sets its path — so with no provider mounted
 * the route was registered and unreachable, and `useSummaryPresentation()` threw
 * for anything that tried. The visible consequence was in History: F-HS-08's
 * **Details** action exists "only while a host can open a summary", so every row
 * in the training record rendered without one. Not a disabled button — an absent
 * one, which is the §8.6 rendering that made the gap invisible.
 *
 * **`HistoryHostProvider`.** `host.tsx`'s default is the empty host, which is
 * the truthful "not composed here" state; mounted, it is the difference between
 * F-HS-20 guessing and F-HS-20 reading. Two of its five capabilities are
 * supplied and three are not, and the split is not arbitrary — see
 * {@link HistoryCapabilities}.
 *
 * The scope sits *above* the shell, and below `JournalScope`: both capabilities
 * it supplies are the Journal's, and reading them from the shared scope is what
 * keeps History looking at the same repository and the same composer session the
 * Journal destination is using. A second repository here would answer "does a
 * recap already exist" from a different set of bytes than the one the composer
 * would then write into.
 */

import type { ReactNode } from "react";
import { useMemo } from "react";

// Imported file by file rather than through each feature's barrel, exactly as
// `journalHost.tsx` does: a barrel import would pull the History screen, the
// summary screen and the recap editor into the eagerly loaded composition root
// and undo the route table's lazy loading of both chunks.
import type { HistoryHost } from "../features/history/host";
import { HistoryHostProvider } from "../features/history/host";
import { useOptionalJournalComposerSession } from "../features/journal/composer/session";
import { useJournalRepository } from "../features/journal/home/journalData";
import { SummaryPresentationProvider } from "../features/summary/presentation";
import type { RecapOpenOutcome, RecapOpener } from "../features/summary/recap";

export interface HistoryScopeProps {
  readonly children?: ReactNode;
}

export function HistoryScope({ children }: HistoryScopeProps): ReactNode {
  return (
    <SummaryPresentationProvider>
      <HistoryCapabilities>{children}</HistoryCapabilities>
    </SummaryPresentationProvider>
  );
}

/**
 * The two History capabilities this composition can honestly supply, and only
 * those two.
 *
 * - **`journal`** (F-HS-20) is the shared repository. Supplying it lets History
 *   resolve "does this session already have a recap" before it presents a
 *   summary, instead of reporting `false` for every session because nothing
 *   could be asked.
 * - **`openRecap`** (F-HS-12) is the composer session's `beginWorkoutRecap`,
 *   which is the same gate every other `begin…` in the product goes through —
 *   so a recap opened from a finished effort resolves entry identity, honours
 *   the Journal vault, and lands in the one composer the route table mounts.
 *
 * `addEffort`, `editEffort` and `repeatPlan` stay absent, and that absence is
 * the truthful state rather than an omission. The first two open Train's manual
 * retrospective sheet, which `TrainHome` owns as local state with no way to
 * raise it from outside the Train root; the third needs Build to accept an
 * inbound plan, and `BuildSurfaces` declares no such slot. Both are seams that
 * do not exist yet, and inventing a control that half-worked would be worse than
 * the inert label §8.6 renders instead. See the packet report.
 *
 * A refusal reports no message. `beginWorkoutRecap` records the sentence it
 * chose in the session's `refusal`, which is React state and therefore not
 * readable in the same tick as the call — so the alert falls back to
 * `RECAP_HANDOFF_COPY.openFailed`, which already ends by saying the effort is
 * still saved. Reading a render-old sentence would be worse than the general
 * one: it could name a refusal from a previous attempt.
 */
function HistoryCapabilities({ children }: HistoryScopeProps): ReactNode {
  const repository = useJournalRepository();
  const session = useOptionalJournalComposerSession();

  const host = useMemo<HistoryHost>(() => {
    if (session === null) {
      return repository === null ? {} : { journal: repository };
    }
    const openRecap: RecapOpener = (request) => {
      const outcome: RecapOpenOutcome = session.beginWorkoutRecap(request)
        ? { kind: "opened" }
        : { kind: "failed" };
      return Promise.resolve(outcome);
    };
    return {
      openRecap,
      ...(repository === null ? {} : { journal: repository }),
    };
  }, [repository, session]);

  return <HistoryHostProvider host={host}>{children}</HistoryHostProvider>;
}
